import { defineStore } from "pinia";
import { PackingSlipPackage } from "./entity";
import { PackingSlipPackageList } from "./list.gen";
import type {
  PackingSlipPackageStore,
  PackingSlipPackageStoreState,
} from "./store.gen.d";
import { analytics, auth } from "@/firebase";
import { type JSONData } from "../../base_classes/dto.d";
import { Pagination } from "../../base_classes/pagination";
import { Query } from "../../base_classes/query";
import { paginationAndQueriesToCacheKey } from "../../base_classes/dto_list";

/// ///////////////////////////////////////////////////////////////////////////////////////////////////
/// WARNING! THIS FILE IS AUTOMATICALLY GENERATED, ANY CHANGES WILL BE LOST ON NEXT GENERATION
/// IF YOU WANT TO EDIT THIS FILE, REMOVE THE .gen. FROM THE FILENAME. THE FILE WILL THEN BE IGNORED.
/// ///////////////////////////////////////////////////////////////////////////////////////////////////

const packingSlipPackageList = new PackingSlipPackageList();
export const usePackingSlipPackageStore = defineStore(
  "packingSlipPackageStore",
  {
    state: (): PackingSlipPackageStoreState => ({
      activeID: null,
      cache: {},
      list: [] as PackingSlipPackage[],
      retrievalTimestamp: 0,
      updateKey: 0,
      storeUpdateTimeout: null,
    }),
    actions: {
      initializeStore(): void {
        this.convertList();
      },
      emitFormUpdateEvent(id: string): void {
        window.dispatchEvent(
          new CustomEvent("form-update-PackingSlipPackage", { detail: { id } }),
        );
      },
      emitStoreUpdateEvent(): void {
        // Clear existing timeout
        if (this.storeUpdateTimeout !== null) {
          clearTimeout(this.storeUpdateTimeout);
        }

        // Set new timeout
        this.storeUpdateTimeout = setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("store.update.PackingSlipPackage"),
          );
          this.storeUpdateTimeout = null;
        }, 1500);
      },
      async activate(packingSlipPackage: PackingSlipPackage): Promise<void> {
        // Set active
        await packingSlipPackage
          .activate(auth.firebaseUser?.uid)
          .then(() => {
            this.activeID = packingSlipPackage.id;
          })
          .catch((error) => {
            console.error(error);
            this.activeID = null;
          });
      },
      add(packingSlipPackage: PackingSlipPackage): void {
        const id = packingSlipPackage.id;
        if (typeof id === "string" && !this.contains(id)) {
          this.list.push(packingSlipPackage);

          // Set active
          if (this.active === null) {
            this.activeID = packingSlipPackage.id;
          }
        } else {
          this.reinsert(packingSlipPackage);
        }
        this.updateKey += 1;
      },
      convertList(): void {
        this.list = (this.list as PackingSlipPackage[]).map((x: any) => {
          const packingSlipPackage = new PackingSlipPackage({});
          packingSlipPackage.set(x as JSONData);
          return packingSlipPackage;
        });
      },
      async delete(packingSlipPackage: PackingSlipPackage): Promise<void> {
        await packingSlipPackage
          .delete()
          .then(() => {
            this.remove(packingSlipPackage);
          })
          .finally(() => {
            this.emitStoreUpdateEvent();
          });
      },
      async deleteById(id: string): Promise<void> {
        const packingSlipPackage = (this.list as PackingSlipPackage[]).find(
          (x: PackingSlipPackage) => x.id === id,
        );
        if (packingSlipPackage !== undefined) {
          await this.delete(packingSlipPackage);
        } else {
          throw new Error("Could not find PackingSlipPackage with id: " + id);
        }
      },
      contains(id: string): boolean {
        return (this.list as PackingSlipPackage[]).some(
          (x: PackingSlipPackage) => x.id === id,
        );
      },
      get(id: string): PackingSlipPackage | null {
        return (
          (this.list as PackingSlipPackage[]).find(
            (x: PackingSlipPackage) => x.id === id,
          ) || null
        );
      },
      reinsert(packingSlipPackage: PackingSlipPackage): void {
        const index = (this.list as PackingSlipPackage[]).findIndex(
          (x: PackingSlipPackage) => x.id === packingSlipPackage.id,
        );
        if (index !== -1) {
          this.list = [
            ...this.list.slice(0, index),
            packingSlipPackage,
            ...this.list.slice(index + 1),
          ];
        } else {
          this.add(packingSlipPackage);
        }
        this.updateKey += 1;
      },
      remove({ id }: PackingSlipPackage): void {
        const index = (this.list as PackingSlipPackage[]).findIndex(
          (x: PackingSlipPackage) => x.id === id,
        );
        if (index !== -1) {
          this.list = [
            ...this.list.slice(0, index),
            ...this.list.slice(index + 1),
          ];
        }
        this.updateKey += 1;
      },
      async retrieve(id: string, force = false): Promise<PackingSlipPackage> {
        // Check if a refresh is required
        if (!force && !this.refreshRequired) {
          const packingSlipPackage = this.get(id);
          if (packingSlipPackage !== null) {
            return packingSlipPackage;
          }
        }

        // Retrieve packingSlipPackage
        const packingSlipPackage = new PackingSlipPackage({});

        // Get packingSlipPackage
        await packingSlipPackage.get([id]).then(async (entity) => {
          this.reinsert(entity);

          // Activate packingSlipPackage
          await this.activate(entity);
        });

        return packingSlipPackage;
      },
      async retrieveAll(force = false): Promise<{
        active: string | null;
        items: PackingSlipPackage[];
      }> {
        // Check if a refresh is required
        if (!force && !this.refreshRequired) {
          return {
            active: this.activeID,
            items: this.list as PackingSlipPackage[],
          };
        }

        // Clear list
        while (this.list.length > 0) {
          this.list.pop();
        }

        // Retrieve list
        await packingSlipPackageList.getAll().then(async (list) => {
          list.forEach((packingSlipPackage) => {
            this.reinsert(packingSlipPackage);
          });

          // Activate packingSlipPackage
          if (list.length > 0) {
            if (packingSlipPackageList.active !== null) {
              this.activeID = packingSlipPackageList.active;
            } else {
              await this.activate(list[0]);
            }
          }
        });

        // Set retrieval timestamp
        this.retrievalTimestamp = Date.now();

        // Set user property
        if (this.activeID !== null) {
          analytics.setUserProperty("packing_slip_package_id", this.activeID);
        }

        return {
          active: this.activeID,
          items: this.list as PackingSlipPackage[],
        };
      },
      async retrievePaginated(
        pagination: Pagination,
        force = false,
      ): Promise<{
        pagination: Pagination;
        items: PackingSlipPackage[];
      }> {
        // Check pagination
        let newPagination = new Pagination(pagination).deepCopy();

        // Get cachekey
        const cacheKey = pagination.toCacheKey();

        // Check if a refresh is required
        if (!force && this.cache[cacheKey]) {
          const cached = this.cache[cacheKey];
          return {
            pagination: cached.pagination.deepCopy(),
            items: cached.ids.map(
              (id: string) => this.get(id) as PackingSlipPackage,
            ),
          };
        }

        // Retrieve list
        const items = await packingSlipPackageList
          .getPaginated([], pagination)
          .then(async ({ items, pagination }) => {
            items.forEach((packingSlipPackage) => {
              this.reinsert(packingSlipPackage);
            });

            // Set new pagination
            newPagination = pagination.deepCopy();

            return items;
          });

        // Set cache
        this.cache[cacheKey] = {
          ids: items.map((x: PackingSlipPackage) => x.id),
          pagination: newPagination,
          retrievalTimestamp: Date.now(),
        };

        // Set retrieval timestamp
        this.retrievalTimestamp = Date.now();

        return {
          pagination: newPagination,
          items,
        };
      },
      async query(
        queries: Query[],
        pagination: Pagination,
        force = false,
      ): Promise<{
        pagination: Pagination;
        items: PackingSlipPackage[];
      }> {
        // Check pagination
        let newPagination = new Pagination(pagination).deepCopy();

        // Get cachekey
        const cacheKey = paginationAndQueriesToCacheKey(pagination, queries);

        // Check if a refresh is required
        if (!force && this.cache[cacheKey]) {
          const cached = this.cache[cacheKey];
          return {
            pagination: cached.pagination.deepCopy(),
            items: cached.ids.map(
              (id: string) => this.get(id) as PackingSlipPackage,
            ),
          };
        }

        // Retrieve list
        const items = await packingSlipPackageList
          .query(queries, pagination)
          .then(async ({ items, pagination }) => {
            items.forEach((packingSlipPackage) => {
              this.reinsert(packingSlipPackage);
            });

            // Set new pagination
            newPagination = pagination.deepCopy();

            return items;
          });

        // Set cache
        this.cache[cacheKey] = {
          ids: items.map((x: PackingSlipPackage) => x.id),
          pagination: newPagination.deepCopy(),
          retrievalTimestamp: Date.now(),
        };

        // Set retrieval timestamp
        this.retrievalTimestamp = Date.now();

        return {
          pagination: newPagination,
          items: items,
        };
      },
      async search(
        query: string,
        pagination: Pagination,
        force = false,
      ): Promise<{
        pagination: Pagination;
        items: PackingSlipPackage[];
      }> {
        // Check pagination
        let newPagination = new Pagination(pagination).deepCopy();

        // Get cachekey
        const cacheKey =
          query.replaceAll(" ", "_") + "-" + pagination.toCacheKey();

        // Check if a refresh is required
        if (!force && this.cache[cacheKey]) {
          const cached = this.cache[cacheKey];
          return {
            items: cached.ids.map(
              (id: string) => this.get(id) as PackingSlipPackage,
            ),
            pagination: cached.pagination.deepCopy(),
          };
        }

        // Retrieve list
        const items = await packingSlipPackageList
          .search(query, pagination)
          .then(async ({ items, pagination }) => {
            items.forEach((packingSlipPackage) => {
              this.reinsert(packingSlipPackage);
            });

            // Set new pagination
            newPagination = pagination.deepCopy();

            return items;
          });

        // Set cache
        this.cache[cacheKey] = {
          ids: items.map((x: PackingSlipPackage) => x.id),
          pagination: newPagination.deepCopy(),
          retrievalTimestamp: Date.now(),
        };

        // Set retrieval timestamp
        this.retrievalTimestamp = Date.now();

        return {
          items: items,
          pagination: newPagination,
        };
      },
      async update(packingSlipPackage: PackingSlipPackage): Promise<string> {
        // Update packingSlipPackage
        await packingSlipPackage.update();

        // Reinsert packingSlipPackage
        this.reinsert(packingSlipPackage);

        // Activate packingSlipPackage
        if (this.active === null) {
          await this.activate(packingSlipPackage);
        }

        this.emitStoreUpdateEvent();

        return packingSlipPackage.id;
      },
    },
    getters: {
      active(): PackingSlipPackage | null {
        if (this.activeID === null) {
          return null;
        }

        const packingSlipPackage = (this.list as PackingSlipPackage[]).find(
          (x: PackingSlipPackage) => x.id === this.activeID,
        );
        if (packingSlipPackage === undefined) {
          return null;
        }

        return packingSlipPackage;
      },
      refreshRequired(): boolean {
        return (
          this.retrievalTimestamp === 0 ||
          this.retrievalTimestamp < Date.now() - 30 * 60000 ||
          this.list.length === 0
        );
      },
    },
  },
);

// Export a function to create a new instance of the store
export function createPackingSlipPackageStore(): PackingSlipPackageStore {
  const store = usePackingSlipPackageStore();
  // Run the initializeStore action when the store is created
  store.initializeStore();
  return store as unknown as PackingSlipPackageStore; // TODO: Fix this
}
